import React, { useState, useEffect, Fragment } from 'react'
import { Transition } from '@headlessui/react'
import { Link } from 'react-router-dom'
import { useConfig, useMultiSite } from 'msp-integrations'
import { transformRegionToMap } from '@utils/pageUtils'
import Icons from '@atoms/Icons/Icons'
import {
  default as TopNewsBannerType,
  TopNewsBannerItemType,
  TopNewsBannerBackgroundColors
} from './TopNewsBanner.types'
import * as TopNewsBannerStyles from './TopNewsBanner.styles'

export const NEXT_NEWS_DELAY = 5000

type TopNewsBannerComponentProps = {
  data: any
  regions: Array<any>
}

const getFormatteNewsItem = (
  newsItem: TopNewsBannerItemType,
  buildUrl: any
) => {
  let newLink = ''
  if (newsItem.link === null || newsItem.link === '#' || newsItem.link === '') {
    newLink = '#'
  }

  if (
    newsItem.link &&
    newsItem.link.indexOf('://') === -1 &&
    newsItem.link !== '/'
  ) {
    newLink = buildUrl(newsItem.link)
  }

  return { ...newsItem, link: newLink }
}

const TopNewsBannerComponent = ({
  data,
  regions = []
}: TopNewsBannerComponentProps) => {
  const { buildUrl } = useMultiSite()

  const topNewsBannerItems =
    transformRegionToMap(regions)?.get('topNewsBannerItems')?.components || []
  const formattedData = {
    ...data,
    closeIcon: <Icons iconName={data.icon} />,
    newsItems: topNewsBannerItems.map((newsItem: TopNewsBannerItemType) => {
      const data = Object.assign({}, newsItem.data || {})
      if (newsItem.custom?.content?.link) {
        data.link = newsItem.custom?.content?.link
      }
      return getFormatteNewsItem(data, buildUrl)
    })
  }

  return <TopNewsBanner {...formattedData} />
}

const TopNewsBannerItem = ({ link, textContent }: TopNewsBannerItemType) => {
  const params = {
    pathname: link
  }

  return (
    <div className={TopNewsBannerStyles.NewsContentWrapper}>
      {link ? <Link to={params}>{textContent}</Link> : textContent}
    </div>
  )
}

export const TopNewsBanner = ({
  backgroundColor = 'black',
  customCssClass = '',
  newsTransitionDelay = NEXT_NEWS_DELAY,
  closeIcon,
  newsItems = []
}: TopNewsBannerType) => {
  const {
    theme: { font }
  } = useConfig()
  const color = TopNewsBannerBackgroundColors[backgroundColor]
  const [showNews, setShowNews] = useState(true)
  const [newsClosed, setNewsClosed] = useState(false)
  const [index, setIndex] = useState(0)
  const [isShowingNews, setIsShowingNews] = useState(true)
  const totalNewsItems = newsItems.length

  const handleNewsClose = () => {
    if (!newsClosed) {
      const topNewsBannerElem = document.getElementById('top-news-banner')

      topNewsBannerElem?.removeAttribute('style')

      setNewsClosed(true)
      setShowNews(false)
    }
  }

  const newsItem: TopNewsBannerItemType = newsItems && newsItems[index]
  const nextNewsItem = () => {
    if (showNews) {
      setIndex((index + 1 + totalNewsItems) % totalNewsItems)
      setIsShowingNews(true)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (totalNewsItems > 1) {
        setIsShowingNews(false)
      }
    }, newsTransitionDelay)

    return () => clearTimeout(timer)
  })

  useEffect(() => {
    if (newsClosed) {
      setShowNews(false)
    }
  }, [newsClosed])

  return (
    <Transition
      as={Fragment}
      show={showNews}
      enter={'ease-in-out duration-500'}
      enterFrom={'-translate-y-full'}
      enterTo={'translate-y-0'}
      leave={'ease-in-out duration-500'}
      leaveFrom={'translate-y-0'}
      leaveTo={'-translate-y-full'}
    >
      {(newsItem && (
        <div
          id='top-news-banner'
          className={`${TopNewsBannerStyles.MainWrapper(
            font.wide
          )} ${color} ${customCssClass}`}
        >
          <Transition
            appear={true}
            show={isShowingNews}
            enter='transition-opacity duration-500'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity duration-500'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
            afterLeave={nextNewsItem}
          >
            <TopNewsBannerItem
              link={newsItem.link}
              textContent={newsItem.textContent}
            />
          </Transition>
          <span
            className={TopNewsBannerStyles.CloseIcon}
            onClick={handleNewsClose}
          >
            {closeIcon}
          </span>
        </div>
      )) || <div></div>}
    </Transition>
  )
}

export default TopNewsBannerComponent
